/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { BACKEND_URL } from "src/constants";
import type { VideoVariant } from "src/store/api/generatedApi";
import { useGcsToken } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import { mediaTimeForDate } from "./hlsSeek";
import {
  applyVideoQuality,
  DEFAULT_VIDEO_QUALITY,
  originalAspectRatio,
} from "./hlsVideoQuality";
import {
  useVideoPlaybackTelemetry,
  VideoPlaybackContext,
} from "./useVideoPlaybackTelemetry";
import {
  NORMAL_VIDEO_BUFFER_BUDGET,
  VideoBufferBudget,
} from "components/scrapEyeDataDashboard/VideoPlayer/constants";

export interface HlsVideoProps {
  srcUrl: string; // absolute or relative URL to .m3u8
  isLive?: boolean; // live vs VOD tuning
  playbackRate?: number; // defaults 1.0
  autoPlay?: boolean; // defaults true
  muted?: boolean; // defaults true (helps autoplay)
  style?: React.CSSProperties;
  className?: string;
  onTokenMissing?: () => void;
  onTokenError?: (error: unknown) => void;
  onPlaylistStart?: (startTimeMs: number) => void;
  positionSeconds?: number;
}

export type UseHlsVideoOptions = HlsVideoProps & {
  bufferBudget?: VideoBufferBudget;
  playbackContext?: VideoPlaybackContext;
  quality?: VideoVariant;
};

const STALL_WATCHDOG_MS = 3_000;
const REMOUNT_DELAY_MS = 2_000;
const MAX_REMOUNT_ATTEMPTS = 5;
const REQUEST_FIRST_BYTE_TIMEOUT_MS = 10_000;
const ERROR_RETRY_INITIAL_DELAY_MS = 500;
const ERROR_RETRY_MAX_DELAY_MS = 8_000;

// Camera recorders produce 15-second segments.
// Live playback trails the playlist by at least one segment.
const SEGMENT_DURATION_SECONDS = 15;
const LIVE_SYNC_CUSHION_SECONDS = 5;
const LIVE_MAX_LATENCY_SEGMENTS = 6;

const MANIFEST_AND_PLAYLIST_LOAD_POLICY = {
  default: {
    maxTimeToFirstByteMs: REQUEST_FIRST_BYTE_TIMEOUT_MS,
    maxLoadTimeMs: 20_000,
    timeoutRetry: {
      maxNumRetry: 4,
      retryDelayMs: 500,
      maxRetryDelayMs: 4_000,
    },
    errorRetry: {
      maxNumRetry: 6,
      retryDelayMs: ERROR_RETRY_INITIAL_DELAY_MS,
      maxRetryDelayMs: ERROR_RETRY_MAX_DELAY_MS,
    },
  },
};

export const computeBufferLength = (
  isLive: boolean,
  playbackRate: number,
  bufferBudget: VideoBufferBudget
) => {
  const baseBufferLength = isLive ? 20 : bufferBudget.baseBufferLengthSeconds;
  return Math.min(
    baseBufferLength * Math.max(1, playbackRate),
    bufferBudget.maxBufferLengthSeconds
  );
};

const seekToLiveEdge = (video: HTMLVideoElement, hls: Hls): void => {
  const livePosition = hls.liveSyncPosition;
  if (livePosition != null && Number.isFinite(livePosition)) {
    video.currentTime = livePosition;
    return;
  }
  const seekable = video.seekable;
  if (seekable.length > 0) {
    video.currentTime = seekable.end(seekable.length - 1);
  }
};

// startLoad() aborts the in-flight segment and refetches it from byte zero.
const isSegmentDownloading = (hls: Hls): boolean =>
  hls.inFlightFragments.main?.state === "FRAG_LOADING";

const setupHls = async ({
  video,
  url,
  autoPlay,
  isLive,
  tokenRef,
  playbackRateRef,
  bufferBudget,
  isActive,
  onPlaylistStart,
  onManifestParsed,
  onRecoverableStall,
  onFatal,
  onUnauthorized,
  onAutoplayBlocked,
  observeHls,
}: {
  video: HTMLVideoElement;
  url: string;
  autoPlay: boolean;
  isLive: boolean;
  tokenRef: React.RefObject<string | undefined>;
  playbackRateRef: React.RefObject<number>;
  bufferBudget: VideoBufferBudget;
  isActive: () => boolean;
  onPlaylistStart?: (startTimeMs: number) => void;
  onManifestParsed: (hls: Hls) => void;
  onRecoverableStall: () => void;
  onFatal: (message: string) => void;
  onUnauthorized: () => void;
  onAutoplayBlocked: () => void;
  observeHls: (hls: Hls) => void;
}) => {
  let authConfig: {
    getIdToken: () => Promise<string | undefined>;
    stateLocale: string | undefined;
    backendUrl: URL;
  } | null = null;
  try {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const getIdToken = async () => auth.currentUser?.getIdToken();
    const stateLocale = (window as unknown as { __STATE_LOCALE__?: string })
      .__STATE_LOCALE__;
    const backendUrl = new URL(BACKEND_URL || "", window.location.origin);
    authConfig = {
      getIdToken,
      stateLocale,
      backendUrl,
    };
  } catch {
    // HLS setup can continue without Firebase credentials.
  }

  if (!isActive()) return null;
  const forwardBufferLength = computeBufferLength(
    isLive,
    playbackRateRef.current,
    bufferBudget
  );
  const hls = new Hls({
    enableWorker: true,

    maxBufferLength: forwardBufferLength,
    maxMaxBufferLength: forwardBufferLength,
    backBufferLength: 5,

    fragLoadPolicy: {
      default: {
        maxTimeToFirstByteMs: REQUEST_FIRST_BYTE_TIMEOUT_MS,
        maxLoadTimeMs: 120_000,
        timeoutRetry: {
          maxNumRetry: 4,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: 4,
          retryDelayMs: ERROR_RETRY_INITIAL_DELAY_MS,
          maxRetryDelayMs: ERROR_RETRY_MAX_DELAY_MS,
        },
      },
    },

    startFragPrefetch: false,
    lowLatencyMode: isLive,
    liveSyncDuration: SEGMENT_DURATION_SECONDS + LIVE_SYNC_CUSHION_SECONDS,
    liveMaxLatencyDuration:
      LIVE_MAX_LATENCY_SEGMENTS * SEGMENT_DURATION_SECONDS,
    maxLiveSyncPlaybackRate: 1.5,

    nudgeMaxRetry: 10,

    manifestLoadPolicy: MANIFEST_AND_PLAYLIST_LOAD_POLICY,
    ...(isLive
      ? {}
      : { playlistLoadPolicy: MANIFEST_AND_PLAYLIST_LOAD_POLICY }),

    maxBufferSize: bufferBudget.maxBufferSizeBytes,
    maxBufferHole: 0.5,

    xhrSetup: function (xhr, url): void | Promise<void> {
      const requestUrl = new URL(url, window.location.href);
      if (requestUrl.origin === authConfig?.backendUrl.origin) {
        const { getIdToken, stateLocale } = authConfig;
        return getIdToken()
          .then((idToken) => {
            if (xhr.readyState === XMLHttpRequest.UNSENT)
              xhr.open("GET", url, true);
            if (idToken)
              xhr.setRequestHeader("Authorization", `Bearer ${idToken}`);
            if (stateLocale)
              xhr.setRequestHeader("Accept-Language", stateLocale);
          })
          .catch(() => undefined);
      } else {
        // Read the token through the ref at send-time so an expired closure
        // value does not outlive a periodic refresh.
        const gcsToken = tokenRef.current;
        if (gcsToken) {
          xhr.setRequestHeader("Authorization", `Bearer ${gcsToken}`);
        }
      }
    },
  });
  observeHls(hls);
  hls.attachMedia(video);

  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    onManifestParsed(hls);
    if (autoPlay)
      video.play().catch((error: unknown) => {
        // An unmuted element (e.g. the live radio feed) whose manifest resolves
        // after the click's transient-user-activation window expires has its
        // play() rejected by the autoplay policy. Surface it so the UI can offer
        // a re-prime instead of sitting silently with a dead meter.
        if (error instanceof DOMException && error.name === "NotAllowedError")
          onAutoplayBlocked();
      });
  });

  // liveSyncPosition and video.seekable are only populated once the first
  // level (media playlist) loads, so seeking to the live edge has to wait
  // for LEVEL_LOADED rather than running inside MANIFEST_PARSED.
  if (isLive) {
    let seeked = false;
    hls.on(Hls.Events.LEVEL_LOADED, () => {
      if (seeked) return;
      seeked = true;
      seekToLiveEdge(video, hls);
    });
  }

  if (onPlaylistStart) {
    let reported = false;
    hls.on(Hls.Events.LEVEL_LOADED, (_evt, data) => {
      if (reported) return;
      const pdt = data.details.fragments[0]?.programDateTime;
      if (pdt != null) {
        reported = true;
        onPlaylistStart(pdt);
      }
    });
  }

  hls.on(Hls.Events.ERROR, (_evt, data) => {
    if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
      onRecoverableStall();
      if (!isSegmentDownloading(hls)) hls.startLoad();
      return;
    }
    if (!data.fatal) return;
    // Non-fatal 401/403s are retried internally by hls.js and usually
    // self-recover once the shared tokenRef has been refreshed. Only ask
    // the provider to refresh when the error escalates to fatal, otherwise
    // a burst of tile-level 401s would fan out into a refresh-per-tile.
    const responseCode = data.response?.code;
    if (responseCode === 401 || responseCode === 403) {
      // onUnauthorized() kicks off an async token refresh that updates
      // tokenRef on a later render; hls.startLoad() fires synchronously
      // and relies on hls.js's own retry loop to pick up the refreshed
      // token on a subsequent attempt.
      onUnauthorized();
      hls.startLoad();
      return;
    }
    // Fatal NETWORK/MEDIA errors survived hls.js's internal retry budget
    // (manifestLoadPolicy.errorRetry) — cheap in-session recovery is spent,
    // so escalate to the bounded-remount path so the overlay surfaces and
    // the retry cap applies.
    onFatal(`${data.type}: ${data.details}`);
  });
  hls.loadSource(url);
  return hls;
};

export const useHlsVideo = ({
  srcUrl,
  isLive = false,
  playbackRate = 1,
  autoPlay = true,
  onTokenMissing,
  onTokenError,
  onPlaylistStart,
  positionSeconds,
  bufferBudget = NORMAL_VIDEO_BUFFER_BUDGET,
  playbackContext,
  quality = DEFAULT_VIDEO_QUALITY,
}: UseHlsVideoOptions) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;
  const qualityRef = useRef(quality);
  qualityRef.current = quality;
  const [levelAspectRatio, setLevelAspectRatio] = useState<number | null>(null);
  const onPlaylistStartRef = useRef(onPlaylistStart);
  onPlaylistStartRef.current = onPlaylistStart;
  const [fatalError, setFatalError] = useState<string | null>(null);
  const { observeHls, trackSeek } = useVideoPlaybackTelemetry({
    videoRef,
    srcUrl: fatalError ? "" : srcUrl,
    playbackContext,
    playbackRate,
  });
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [remountCounter, setRemountCounter] = useState(0);
  const remountAttemptRef = useRef(0);
  const prevRemountCounterRef = useRef(0);
  const { gcsToken, error: tokenError, refresh } = useGcsToken();
  const token = gcsToken?.access_token;
  const expiresAt = gcsToken?.expires_at;
  const tokenRef = useRef<string | undefined>(token);
  tokenRef.current = token;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const isTokenValid = useMemo(() => {
    if (!token) return false;
    if (!expiresAt) return false;
    return new Date(expiresAt) > new Date();
  }, [token, expiresAt]);

  useEffect(() => {
    if (tokenError && onTokenError) {
      onTokenError(tokenError);
    }
  }, [tokenError, onTokenError]);

  const url = useMemo(() => srcUrl, [srcUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url || url === "" || !isTokenValid || !tokenRef.current) {
      if (!tokenRef.current && onTokenMissing) onTokenMissing();
      return;
    }

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setFatalError(null);
    setLevelAspectRatio(null);
    setIsReconnecting(false);
    setAutoplayBlocked(false);
    // Reset the remount budget only on a fresh mount (URL/token/live mode
    // change). Effect re-runs triggered by our own setRemountCounter inside
    // onFatal must preserve the counter, otherwise the cap is unreachable
    // and playback loops at a fixed 2s backoff forever.
    const isRemountReentry = prevRemountCounterRef.current !== remountCounter;
    prevRemountCounterRef.current = remountCounter;
    if (!isRemountReentry) {
      remountAttemptRef.current = 0;
    }

    if (Hls.isSupported()) {
      let isActive = true;
      let stallTimer: ReturnType<typeof setTimeout> | null = null;
      let remountTimer: ReturnType<typeof setTimeout> | null = null;

      const clearStallTimer = () => {
        if (stallTimer) {
          clearTimeout(stallTimer);
          stallTimer = null;
        }
      };

      const armStallWatchdog = () => {
        clearStallTimer();
        stallTimer = setTimeout(() => {
          const hls = hlsRef.current;
          if (!hls || !isActive) return;
          if (isSegmentDownloading(hls)) {
            armStallWatchdog();
            return;
          }
          hls.startLoad();
          if (isLive) seekToLiveEdge(video, hls);
        }, STALL_WATCHDOG_MS);
      };

      const onStalled = () => {
        armStallWatchdog();
      };
      const onWaiting = () => {
        armStallWatchdog();
      };
      const onPlaying = () => {
        clearStallTimer();
        setAutoplayBlocked(false);
      };
      const onTimeUpdate = () => {
        clearStallTimer();
      };

      video.addEventListener("stalled", onStalled);
      video.addEventListener("waiting", onWaiting);
      video.addEventListener("playing", onPlaying);
      video.addEventListener("timeupdate", onTimeUpdate);

      setupHls({
        video,
        url,
        autoPlay,
        isLive,
        tokenRef,
        playbackRateRef,
        bufferBudget,
        isActive: () => isActive,
        onPlaylistStart: onPlaylistStartRef.current,
        onManifestParsed: (hls) => {
          remountAttemptRef.current = 0;
          applyVideoQuality(hls, qualityRef.current);
          const originalLevelAspectRatio = originalAspectRatio(hls.levels);
          hls.on(Hls.Events.LEVEL_SWITCHED, (_event, { level }) => {
            if (!isActive) return;
            setLevelAspectRatio(
              hls.levels[level]?.name === "low"
                ? originalLevelAspectRatio
                : null
            );
          });
        },
        onRecoverableStall: armStallWatchdog,
        onFatal: (message) => {
          if (!isActive) return;
          // A second fatal on the same instance before the remount has
          // fired would leak the prior setTimeout and double-bump the
          // attempt counter. Coalesce: one pending remount per setup.
          if (remountTimer) return;
          if (remountAttemptRef.current >= MAX_REMOUNT_ATTEMPTS) {
            console.error(
              "HLS fatal error — remount attempts exhausted:",
              message
            );
            setIsReconnecting(false);
            setFatalError(
              `Unrecoverable HLS error after ${MAX_REMOUNT_ATTEMPTS} reconnect attempts: ${message}`
            );
            return;
          }
          const backoffMs = REMOUNT_DELAY_MS * 2 ** remountAttemptRef.current;
          remountAttemptRef.current += 1;
          setIsReconnecting(true);
          console.error(
            `HLS fatal error — scheduled attempt ${remountAttemptRef.current} of ${MAX_REMOUNT_ATTEMPTS} in ${backoffMs}ms:`,
            message
          );
          remountTimer = setTimeout(() => {
            if (!isActive) return;
            setIsReconnecting(false);
            setRemountCounter((n) => n + 1);
          }, backoffMs);
        },
        onUnauthorized: () => {
          refreshRef.current();
        },
        onAutoplayBlocked: () => {
          if (isActive) setAutoplayBlocked(true);
        },
        observeHls,
      })
        .then((hls) => {
          if (!hls) return;
          if (isActive) {
            hlsRef.current = hls;
          } else {
            hls.destroy();
          }
        })
        .catch((error: unknown) => {
          if (isActive && error instanceof Error) {
            setFatalError(`Unrecoverable HLS error: ${error.message}`);
            hlsRef.current = null;
          }
        });

      return () => {
        isActive = false;
        clearStallTimer();
        if (remountTimer) clearTimeout(remountTimer);
        video.removeEventListener("stalled", onStalled);
        video.removeEventListener("waiting", onWaiting);
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("timeupdate", onTimeUpdate);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
      if (autoPlay)
        video.play().catch((error: unknown) => {
          if (error instanceof DOMException && error.name === "NotAllowedError")
            setAutoplayBlocked(true);
        });
      return () => {
        video.src = "";
      };
    } else {
      setFatalError("HLS not supported in this browser");
    }
    // `xhrSetup` reads `tokenRef`. The dependency list therefore omits `token`.
    // Reading `tokenRef` permits silent refreshes without recreating HLS.
    // `isTokenValid` reruns the effect when the token becomes usable.
  }, [
    url,
    isLive,
    autoPlay,
    isTokenValid,
    onTokenMissing,
    remountCounter,
    bufferBudget,
    observeHls,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (positionSeconds === undefined) return;
    if (!Number.isFinite(positionSeconds)) return;

    const seek = () => {
      try {
        video.currentTime = positionSeconds;
      } catch {
        /* ignore */
      }
    };

    if (video.readyState >= 1) {
      seek();
      return;
    }

    const onLoadedMetadata = () => {
      seek();
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, [positionSeconds]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = playbackRate ?? 1;
    const hls = hlsRef.current;
    if (hls) {
      const buf = computeBufferLength(isLive, playbackRate, bufferBudget);
      hls.config.maxBufferLength = buf;
      hls.config.maxMaxBufferLength = buf;
    }
  }, [playbackRate, isLive, bufferBudget]);

  useEffect(() => {
    const hls = hlsRef.current;
    if (hls) applyVideoQuality(hls, quality);
  }, [quality]);

  useEffect(() => {
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls || !isLive) return;

    const onLevelLoaded = () => {
      if (video.paused && video.readyState >= 3) {
        video.play().catch((error: unknown) => {
          if (error instanceof DOMException && error.name === "NotAllowedError")
            setAutoplayBlocked(true);
        });
      }
    };
    hls.on(Hls.Events.LEVEL_LOADED, onLevelLoaded);
    return () => {
      hls.off(Hls.Events.LEVEL_LOADED, onLevelLoaded);
    };
  }, [isLive]);

  // HLS reports a hole-aware buffer end; video.buffered can extend past gaps.
  // Missing HLS data triggers dead-tile recovery.
  const getForwardBufferEnd = useCallback((): number | null => {
    return hlsRef.current?.mainForwardBufferInfo?.end ?? null;
  }, []);

  // Manifest timestamps align transcript words despite gaps in media time.
  // No wall-clock time exists before the first fragment loads.
  const getPlayingDate = useCallback((): Date | null => {
    return hlsRef.current?.playingDate ?? null;
  }, []);

  // Fragment timestamps account for silent gaps between media-time intervals.
  const seekToDate = useCallback((dateMs: number): boolean => {
    const hls = hlsRef.current;
    const video = videoRef.current;
    if (!hls || !video) return false;
    const level = [
      hls.levels[hls.currentLevel],
      hls.levels[hls.loadLevel],
      ...hls.levels,
    ].find((candidate) => candidate?.details?.fragments.length);
    const fragments = level?.details?.fragments ?? [];
    const mediaTime = mediaTimeForDate(
      fragments.map((fragment) => ({
        start: fragment.start,
        duration: fragment.duration,
        programDateTime: fragment.programDateTime ?? null,
      })),
      dateMs
    );
    if (mediaTime == null) return false;
    video.currentTime = mediaTime;
    return true;
  }, []);

  const skipSeconds = useCallback((seconds: number): void => {
    const video = videoRef.current;
    if (!video) return;
    const end = Number.isFinite(video.duration) ? video.duration : Infinity;
    video.currentTime = Math.min(Math.max(video.currentTime + seconds, 0), end);
  }, []);

  return {
    ref: videoRef,
    fatalError,
    isReconnecting,
    autoplayBlocked,
    getForwardBufferEnd,
    getPlayingDate,
    seekToDate,
    skipSeconds,
    trackSeek,
    levelAspectRatio,
  };
};
